import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogOut, Users, Trash2, ArrowLeft } from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [websiteName, setWebsiteName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoSize, setLogoSize] = useState(40);
  const [showTitle, setShowTitle] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => {
    loadSettings();
    loadProfile();
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setWebsiteName(response.data.website_name || '');
      setCurrentLogo(response.data.logo_url || null);
      setLogoSize(response.data.logo_size || 40);
      setShowTitle(response.data.show_title !== false);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadProfile = () => {
    setProfileName(user?.name || '');
    setProfileEmail(user?.email || '');
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);

    const formData = new FormData();
    if (websiteName) formData.append('website_name', websiteName);
    if (logoFile) formData.append('logo', logoFile);
    formData.append('logo_size', logoSize);
    formData.append('show_title', showTitle);

    try {
      await axios.post(`${API}/settings`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Settings updated successfully');
      loadSettings();
      setLogoFile(null);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (newPassword && newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingProfile(true);

    try {
      await axios.put(`${API}/profile`, {
        name: profileName !== user?.name ? profileName : null,
        email: profileEmail !== user?.email ? profileEmail : null,
        current_password: currentPassword || null,
        new_password: newPassword || null
      });
      toast.success('Profile updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);

    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('User added successfully');
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      setShowAddUser(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Desktop Header */}
          <div className="hidden sm:flex items-center justify-between">
            <AppHeader title="Vistara Verify" subtitle="Settings" />
            
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} size="sm">
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to</span> Dashboard
              </Button>
              <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2" size="sm">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="flex sm:hidden items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <span className="font-semibold text-slate-900">Settings</span>
            </div>
            <Button variant="outline" onClick={handleLogout} size="sm" className="p-2">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <Tabs defaultValue="profile" className="space-y-4 sm:space-y-6">
          {/* Tab Navigation - Scrollable on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className={`inline-flex w-auto min-w-full sm:w-full sm:max-w-md h-11 sm:h-12 p-1 bg-slate-100 rounded-lg ${isAdmin ? 'sm:grid sm:grid-cols-3' : ''}`}>
              <TabsTrigger value="profile" className="flex-1 min-w-[100px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4">
                Profile
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="app" className="flex-1 min-w-[100px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4">
                  App Settings
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="users" className="flex-1 min-w-[100px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4">
                  Users
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="profile">
            <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8 max-w-2xl">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Your Profile</h2>
              
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Name</Label>
                  <Input
                    id="profile-name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    className="h-10 sm:h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input
                    id="profile-email"
                    type="email"
                    value={profileEmail}
                    onChange={(e) => setProfileEmail(e.target.value)}
                    className="h-10 sm:h-11"
                  />
                </div>

                <div className="border-t pt-4 mt-4 sm:mt-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Change Password</h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="h-10 sm:h-11"
                        placeholder="Leave blank to keep current"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="h-10 sm:h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="h-10 sm:h-11"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={savingProfile}
                  className="w-full h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white mt-4"
                >
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </Button>
              </form>
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="app">
              <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8 max-w-2xl">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">App Settings</h2>
                
                <form onSubmit={handleSaveSettings} className="space-y-4 sm:space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="website-name">Website Name</Label>
                    <Input
                      id="website-name"
                      value={websiteName}
                      onChange={(e) => setWebsiteName(e.target.value)}
                      className="h-10 sm:h-11"
                      placeholder="Vistara Verify"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logo-upload">Logo</Label>
                    {currentLogo && (
                      <div className="mb-2 p-3 sm:p-4 bg-slate-50 rounded-md flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                        <img 
                          src={`${BACKEND_URL}${currentLogo}`} 
                          alt="Current Logo" 
                          style={{ height: `${logoSize}px` }}
                          className="object-contain"
                        />
                        <span className="text-sm text-slate-600">Current logo preview</span>
                      </div>
                    )}
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setLogoFile(e.target.files[0])}
                      className="h-10 sm:h-11"
                    />
                    <p className="text-xs text-slate-500">Upload a new logo to replace the current one</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logo-size">Logo Size: {logoSize}px</Label>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="text-xs text-slate-500 w-8 sm:w-12">Small</span>
                      <input
                        id="logo-size"
                        type="range"
                        min="20"
                        max="100"
                        value={logoSize}
                        onChange={(e) => setLogoSize(parseInt(e.target.value))}
                        className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #0F172A 0%, #0F172A ${((logoSize - 20) / 80) * 100}%, #E2E8F0 ${((logoSize - 20) / 80) * 100}%, #E2E8F0 100%)`
                        }}
                      />
                      <span className="text-xs text-slate-500 w-8 sm:w-12 text-right">Large</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg gap-4">
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="show-title" className="text-sm sm:text-base font-medium">Show Title</Label>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1 truncate">
                        Display "{websiteName || 'Vistara Verify'}"
                      </p>
                    </div>
                    <button
                      id="show-title"
                      type="button"
                      onClick={() => setShowTitle(!showTitle)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                        showTitle ? 'bg-slate-900' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showTitle ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={savingSettings}
                    className="w-full h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {savingSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                </form>
              </div>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="users">
              <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">User Management</h2>
                  <Button
                    onClick={() => setShowAddUser(!showAddUser)}
                    className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
                    size="sm"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>

                {showAddUser && (
                  <form onSubmit={handleAddUser} className="mb-4 sm:mb-6 p-3 sm:p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-user-name">Name</Label>
                        <Input
                          id="new-user-name"
                          value={newUser.name}
                          onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                          required
                          className="h-10 sm:h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-user-email">Email</Label>
                        <Input
                          id="new-user-email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          required
                          className="h-10 sm:h-11"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-user-password">Password</Label>
                        <Input
                          id="new-user-password"
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                          required
                          className="h-10 sm:h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-user-role">Role</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                          <SelectTrigger className="h-10 sm:h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        type="submit"
                        disabled={addingUser}
                        className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
                      >
                        {addingUser ? 'Adding...' : 'Add User'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAddUser(false)}
                        className="w-full sm:w-auto"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {loadingUsers ? (
                  <div className="text-center py-8 sm:py-12">Loading users...</div>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {users.map((u) => (
                      <div key={u.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border border-slate-200 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{u.name}</p>
                          <p className="text-sm text-slate-500 truncate">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            u.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'
                          }`}>
                            {u.role}
                          </span>
                          {u.id !== user?.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
